import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTenant } from '../context/TenantContext';

const API_BASE = process.env.REACT_APP_API_URL || '';

function formatActivity(date) {
  if (!date) return 'Never';
  const d = new Date(date);
  return d.toLocaleString('de-DE', { dateStyle: 'short', timeStyle: 'short' });
}

export default function AdminPanel({ onBack }) {
  const { token } = useAuth();
  const { loadTenants } = useTenant();
  const [tab, setTab] = useState('users');

  // ── Users ──────────────────────────────────────────────────────────────
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(true);
  const [editingUser, setEditingUser] = useState(null);
  const [editForm, setEditForm] = useState({ password: '', role: '', canCreateTenants: false, canViewAllTasks: false, canManageTaskStatuses: false });
  const [editError, setEditError] = useState('');
  const [editSaving, setEditSaving] = useState(false);
  const [creatingUser, setCreatingUser] = useState(false);
  const [createForm, setCreateForm] = useState({ username: '', email: '', password: '', role: 'user', canCreateTenants: false, canViewAllTasks: false, canManageTaskStatuses: false });
  const [createError, setCreateError] = useState('');
  const [createSaving, setCreateSaving] = useState(false);

  const loadUsers = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/api/admin/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setUsers(data);
    } catch (err) {
      console.error(err);
    } finally {
      setUsersLoading(false);
    }
  }, [token]);

  useEffect(() => { loadUsers(); }, [loadUsers]);

  const toggleUser = async (userId, currentlyActive) => {
    const action = currentlyActive ? 'deactivate' : 'activate';
    try {
      const res = await fetch(`${API_BASE}/api/admin/users/${userId}/${action}`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        setUsers(prev => prev.map(u =>
          u._id === userId ? { ...u, active: !currentlyActive } : u
        ));
      } else {
        const data = await res.json();
        alert(data.error);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const deleteUser = async (userId, username) => {
    if (!window.confirm(`Benutzer "${username}" wirklich löschen?`)) return;
    try {
      const res = await fetch(`${API_BASE}/api/admin/users/${userId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        setUsers(prev => prev.filter(u => u._id !== userId));
      } else {
        const data = await res.json();
        alert(data.error);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const openEdit = (user) => {
    setEditingUser(user);
    setEditForm({
      password: '',
      role: user.role,
      canCreateTenants: !!user.canCreateTenants,
      canViewAllTasks: !!user.canViewAllTasks,
      canManageTaskStatuses: !!user.canManageTaskStatuses
    });
    setEditError('');
  };

  const saveEdit = async () => {
    setEditSaving(true);
    setEditError('');
    const updates = {};
    if (editForm.password) updates.password = editForm.password;
    if (editForm.role !== editingUser.role) updates.role = editForm.role;
    if (editForm.canCreateTenants !== !!editingUser.canCreateTenants) {
      updates.canCreateTenants = editForm.canCreateTenants;
    }
    if (editForm.canViewAllTasks !== !!editingUser.canViewAllTasks) {
      updates.canViewAllTasks = editForm.canViewAllTasks;
    }
    if (editForm.canManageTaskStatuses !== !!editingUser.canManageTaskStatuses) {
      updates.canManageTaskStatuses = editForm.canManageTaskStatuses;
    }

    if (Object.keys(updates).length === 0) {
      setEditingUser(null);
      setEditSaving(false);
      return;
    }

    try {
      const res = await fetch(`${API_BASE}/api/admin/users/${editingUser._id}`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(updates)
      });
      const data = await res.json();
      if (!res.ok) {
        setEditError(data.error);
        return;
      }
      setUsers(prev => prev.map(u => u._id === editingUser._id ? { ...u, ...data } : u));
      setEditingUser(null);
    } catch (err) {
      setEditError(err.message);
    } finally {
      setEditSaving(false);
    }
  };

  const openCreate = () => {
    setCreateForm({ username: '', email: '', password: '', role: 'user', canCreateTenants: false, canViewAllTasks: false });
    setCreateError('');
    setCreatingUser(true);
  };

  const saveCreate = async () => {
    setCreateSaving(true);
    setCreateError('');
    const { username, email, password, role, canCreateTenants, canViewAllTasks, canManageTaskStatuses } = createForm;
    if (!username.trim() || !email.trim() || !password) {
      setCreateError('Benutzername, E-Mail und Passwort sind erforderlich.');
      setCreateSaving(false);
      return;
    }
    try {
      const res = await fetch(`${API_BASE}/api/admin/users`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: username.trim(), email: email.trim(), password, role, canCreateTenants, canViewAllTasks, canManageTaskStatuses })
      });
      const data = await res.json();
      if (!res.ok) {
        setCreateError(data.error || 'Fehler beim Erstellen.');
        return;
      }
      setUsers(prev => [...prev, data]);
      setCreatingUser(false);
    } catch (err) {
      setCreateError(err.message);
    } finally {
      setCreateSaving(false);
    }
  };

  const pendingUsers = users.filter(u => u.active === false);
  const activeUsers = users
    .filter(u => u.active !== false)
    .sort((a, b) => {
      if (!a.lastActivity && !b.lastActivity) return 0;
      if (!a.lastActivity) return 1;
      if (!b.lastActivity) return -1;
      return new Date(b.lastActivity) - new Date(a.lastActivity);
    });

  // ── Tenants (SuperAdmin) ───────────────────────────────────────────────
  const [adminTenants, setAdminTenants] = useState([]);
  const [tenantsLoading, setTenantsLoading] = useState(false);
  const [tenantBusy, setTenantBusy] = useState(null); // tenantId, der gerade verarbeitet wird
  const [creatingTenant, setCreatingTenant] = useState(false);
  const [tenantCreateName, setTenantCreateName] = useState('');
  const [tenantCreateError, setTenantCreateError] = useState('');
  const [tenantCreateSaving, setTenantCreateSaving] = useState(false);

  const openCreateTenant = () => {
    setTenantCreateName('');
    setTenantCreateError('');
    setCreatingTenant(true);
  };

  const saveCreateTenant = async () => {
    const name = tenantCreateName.trim();
    if (!name) {
      setTenantCreateError('Name ist erforderlich.');
      return;
    }
    setTenantCreateSaving(true);
    setTenantCreateError('');
    try {
      const res = await fetch(`${API_BASE}/api/tenants`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ name })
      });
      const data = await res.json();
      if (!res.ok) {
        setTenantCreateError(data.error || 'Fehler beim Erstellen.');
        return;
      }
      // Neu angelegter Mandant erscheint mit memberCount=1 (der Admin selbst ist Owner)
      setAdminTenants(prev => [
        ...prev,
        { ...data.tenant, active: true, memberCount: 1 }
      ]);
      // Sidebar-Switcher refreshen, damit der Admin direkt in den neuen Mandanten wechseln kann
      loadTenants();
      setCreatingTenant(false);
    } catch (err) {
      setTenantCreateError(err.message);
    } finally {
      setTenantCreateSaving(false);
    }
  };

  const loadAdminTenants = useCallback(async () => {
    setTenantsLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/admin/tenants`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setAdminTenants(data.tenants || []);
    } catch (err) {
      console.error(err);
      setAdminTenants([]);
    } finally {
      setTenantsLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (tab === 'tenants') loadAdminTenants();
  }, [tab, loadAdminTenants]);

  const toggleTenantActive = async (tenant) => {
    const action = tenant.active ? 'deactivate' : 'activate';
    setTenantBusy(tenant._id);
    try {
      const res = await fetch(`${API_BASE}/api/admin/tenants/${tenant._id}/${action}`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) {
        const data = await res.json();
        alert(data.error || 'Fehler');
        return;
      }
      setAdminTenants(prev => prev.map(t =>
        t._id === tenant._id ? { ...t, active: !tenant.active } : t
      ));
    } finally {
      setTenantBusy(null);
    }
  };

  const deleteTenant = async (tenant) => {
    if (!window.confirm(
      `Mandant "${tenant.name}" wirklich endgültig löschen?\n\n` +
      `ALLES wird unwiderruflich entfernt:\n` +
      `  • ${tenant.memberCount} Mitgliedschaft(en)\n` +
      `  • Alle Workflows + Run-Historie\n` +
      `  • Alle Datenquellen + SMTP-Konfig\n` +
      `  • Alle Abteilungen + Mitglieder\n` +
      `  • Alle Aufgaben (offen + erledigt)\n` +
      `  • Alle Benachrichtigungen\n\n` +
      `Chats und User-Accounts bleiben erhalten.`
    )) return;
    setTenantBusy(tenant._id);
    try {
      const res = await fetch(`${API_BASE}/api/admin/tenants/${tenant._id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) {
        const data = await res.json();
        alert(data.error || 'Fehler');
        return;
      }
      const data = await res.json();
      if (data.cascade) {
        const summary = Object.entries(data.cascade)
          .filter(([, n]) => n > 0)
          .map(([k, n]) => `${k}: ${n}`)
          .join(', ');
        if (summary) console.log(`[admin] cascade for "${tenant.name}":`, summary);
      }
      setAdminTenants(prev => prev.filter(t => t._id !== tenant._id));
    } finally {
      setTenantBusy(null);
    }
  };

  // ── Models ─────────────────────────────────────────────────────────────
  const [models, setModels] = useState([]);
  const [modelsLoading, setModelsLoading] = useState(false);
  const [editingNames, setEditingNames] = useState({});
  const [savingModel, setSavingModel] = useState(null);

  const loadModels = useCallback(async () => {
    setModelsLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/models`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.models) {
        setModels(data.models);
        const initial = {};
        for (const m of data.models) initial[m.name] = m.displayName || '';
        setEditingNames(initial);
      } else {
        setModels([]);
      }
    } catch (err) {
      setModels([]);
    } finally {
      setModelsLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (tab === 'models') loadModels();
  }, [tab, loadModels]);

  const saveDisplayName = async (modelName) => {
    setSavingModel(modelName);
    try {
      await fetch(`${API_BASE}/api/models/display-name`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ modelName, displayName: editingNames[modelName] || null })
      });
      setModels(prev => prev.map(m =>
        m.name === modelName ? { ...m, displayName: editingNames[modelName] || null } : m
      ));
    } catch (err) {
      console.error(err);
    } finally {
      setSavingModel(null);
    }
  };

  return (
    <div className="admin-panel">
      <div className="admin-header">
        <button className="icon-btn" onClick={onBack} title="Back">←</button>
        <h2>Administration</h2>
      </div>

      <div className="admin-tabs">
        <button className={`admin-tab${tab === 'users' ? ' active' : ''}`} onClick={() => setTab('users')}>
          Benutzer
        </button>
        <button className={`admin-tab${tab === 'tenants' ? ' active' : ''}`} onClick={() => setTab('tenants')}>
          Mandanten
        </button>
        <button className={`admin-tab${tab === 'models' ? ' active' : ''}`} onClick={() => setTab('models')}>
          Modelle
        </button>
      </div>

      {/* ── Create User Modal ── */}
      {creatingUser && (
        <div className="modal-backdrop" onClick={() => setCreatingUser(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Neuer Benutzer</h3>
              <button className="icon-btn" onClick={() => setCreatingUser(false)}>✕</button>
            </div>
            {createError && <div className="error-message small">{createError}</div>}
            <div className="modal-body">
              <label className="modal-label">Benutzername</label>
              <input
                className="modal-input"
                value={createForm.username}
                onChange={e => setCreateForm(prev => ({ ...prev, username: e.target.value }))}
                autoFocus
              />
              <label className="modal-label">E-Mail</label>
              <input
                type="email"
                className="modal-input"
                value={createForm.email}
                onChange={e => setCreateForm(prev => ({ ...prev, email: e.target.value }))}
              />
              <label className="modal-label">Passwort</label>
              <input
                type="password"
                className="modal-input"
                placeholder="Mindestens 6 Zeichen"
                value={createForm.password}
                onChange={e => setCreateForm(prev => ({ ...prev, password: e.target.value }))}
              />
              <label className="modal-label">Rolle</label>
              <select
                className="modal-input"
                value={createForm.role}
                onChange={e => setCreateForm(prev => ({ ...prev, role: e.target.value }))}
              >
                <option value="user">User</option>
                <option value="admin">Admin</option>
              </select>
              <label className="modal-checkbox-label">
                <input
                  type="checkbox"
                  checked={createForm.role === 'admin' ? true : createForm.canCreateTenants}
                  disabled={createForm.role === 'admin'}
                  onChange={e => setCreateForm(prev => ({ ...prev, canCreateTenants: e.target.checked }))}
                />
                <span>
                  Darf Mandanten erstellen
                  {createForm.role === 'admin' && (
                    <em className="modal-hint"> (Admin: implizit erlaubt)</em>
                  )}
                </span>
              </label>
              <label className="modal-checkbox-label">
                <input
                  type="checkbox"
                  checked={createForm.role === 'admin' ? true : createForm.canViewAllTasks}
                  disabled={createForm.role === 'admin'}
                  onChange={e => setCreateForm(prev => ({ ...prev, canViewAllTasks: e.target.checked }))}
                />
                <span>
                  Darf alle Aufgaben sehen
                  {createForm.role === 'admin' && (
                    <em className="modal-hint"> (Admin: implizit erlaubt)</em>
                  )}
                </span>
              </label>
              <label className="modal-checkbox-label">
                <input
                  type="checkbox"
                  checked={createForm.role === 'admin' ? true : createForm.canManageTaskStatuses}
                  disabled={createForm.role === 'admin'}
                  onChange={e => setCreateForm(prev => ({ ...prev, canManageTaskStatuses: e.target.checked }))}
                />
                <span>
                  Darf Aufgaben-Status verwalten
                  {createForm.role === 'admin' && (
                    <em className="modal-hint"> (Admin: implizit erlaubt)</em>
                  )}
                </span>
              </label>
            </div>
            <div className="modal-actions">
              <button className="btn-sm-primary" onClick={saveCreate} disabled={createSaving}>
                {createSaving ? '...' : 'Erstellen'}
              </button>
              <button className="btn-sm-cancel" onClick={() => setCreatingUser(false)}>Abbrechen</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Edit User Modal ── */}
      {editingUser && (
        <div className="modal-backdrop" onClick={() => setEditingUser(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Bearbeiten: {editingUser.username}</h3>
              <button className="icon-btn" onClick={() => setEditingUser(null)}>✕</button>
            </div>
            {editError && <div className="error-message small">{editError}</div>}
            <div className="modal-body">
              <label className="modal-label">Neues Passwort</label>
              <input
                type="password"
                className="modal-input"
                placeholder="Leer lassen = nicht ändern"
                value={editForm.password}
                onChange={e => setEditForm(prev => ({ ...prev, password: e.target.value }))}
              />
              <label className="modal-label">Rolle</label>
              <select
                className="modal-input"
                value={editForm.role}
                onChange={e => setEditForm(prev => ({ ...prev, role: e.target.value }))}
              >
                <option value="user">User</option>
                <option value="admin">Admin</option>
              </select>
              <label className="modal-checkbox-label">
                <input
                  type="checkbox"
                  checked={editForm.role === 'admin' ? true : editForm.canCreateTenants}
                  disabled={editForm.role === 'admin'}
                  onChange={e => setEditForm(prev => ({ ...prev, canCreateTenants: e.target.checked }))}
                />
                <span>
                  Darf Mandanten erstellen
                  {editForm.role === 'admin' && (
                    <em className="modal-hint"> (Admin: implizit erlaubt)</em>
                  )}
                </span>
              </label>
              <label className="modal-checkbox-label">
                <input
                  type="checkbox"
                  checked={editForm.role === 'admin' ? true : editForm.canViewAllTasks}
                  disabled={editForm.role === 'admin'}
                  onChange={e => setEditForm(prev => ({ ...prev, canViewAllTasks: e.target.checked }))}
                />
                <span>
                  Darf alle Aufgaben sehen
                  {editForm.role === 'admin' && (
                    <em className="modal-hint"> (Admin: implizit erlaubt)</em>
                  )}
                </span>
              </label>
              <label className="modal-checkbox-label">
                <input
                  type="checkbox"
                  checked={editForm.role === 'admin' ? true : editForm.canManageTaskStatuses}
                  disabled={editForm.role === 'admin'}
                  onChange={e => setEditForm(prev => ({ ...prev, canManageTaskStatuses: e.target.checked }))}
                />
                <span>
                  Darf Aufgaben-Status verwalten
                  {editForm.role === 'admin' && (
                    <em className="modal-hint"> (Admin: implizit erlaubt)</em>
                  )}
                </span>
              </label>
            </div>
            <div className="modal-actions">
              <button className="btn-sm-primary" onClick={saveEdit} disabled={editSaving}>
                {editSaving ? '...' : 'Speichern'}
              </button>
              <button className="btn-sm-cancel" onClick={() => setEditingUser(null)}>Abbrechen</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Users Tab ── */}
      {tab === 'users' && (
        usersLoading ? (
          <div className="admin-loading">Benutzer werden geladen...</div>
        ) : (
          <div className="admin-content">
            {pendingUsers.length > 0 && (
              <div className="admin-section">
                <h3 className="admin-section-title">
                  Ausstehende Aktivierung
                  <span className="badge">{pendingUsers.length}</span>
                </h3>
                <div className="user-list">
                  {pendingUsers.map(user => (
                    <div key={user._id} className="user-card pending">
                      <div className="user-card-info">
                        <div className="user-card-avatar">{user.username[0].toUpperCase()}</div>
                        <div>
                          <div className="user-card-name">{user.username}</div>
                          <div className="user-card-email">{user.email}</div>
                        </div>
                      </div>
                      <button className="btn-activate" onClick={() => toggleUser(user._id, false)}>
                        Aktivieren
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="admin-section">
              <h3 className="admin-section-title">
                Aktive Benutzer
                <button className="btn-sm-primary" style={{ marginLeft: 'auto', flex: '0 0 auto' }} onClick={openCreate}>
                  + Benutzer hinzufügen
                </button>
              </h3>
              <div className="user-list">
                {activeUsers.map(user => (
                  <div key={user._id} className="user-card">
                    <div className="user-card-info">
                      <div className="user-card-avatar">{user.username[0].toUpperCase()}</div>
                      <div>
                        <div className="user-card-name">
                          {user.username}
                          {user.role === 'admin' && <span className="role-badge">Admin</span>}
                        </div>
                        <div className="user-card-email">{user.email}</div>
                        <div className="user-card-activity">
                          Letzter Login: {formatActivity(user.lastActivity)}
                        </div>
                      </div>
                    </div>
                    <div className="user-card-actions">
                      <button className="btn-edit" onClick={() => openEdit(user)} title="Bearbeiten">
                        ✏️
                      </button>
                      {user.role !== 'admin' && (
                        <>
                          <button className="btn-deactivate" onClick={() => toggleUser(user._id, true)}>
                            Deaktivieren
                          </button>
                          <button className="btn-delete" onClick={() => deleteUser(user._id, user.username)} title="Löschen">
                            🗑️
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )
      )}

      {/* ── Create Tenant Modal ── */}
      {creatingTenant && (
        <div className="modal-backdrop" onClick={() => setCreatingTenant(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Neuer Mandant</h3>
              <button className="icon-btn" onClick={() => setCreatingTenant(false)}>✕</button>
            </div>
            {tenantCreateError && <div className="error-message small">{tenantCreateError}</div>}
            <div className="modal-body">
              <label className="modal-label">Name</label>
              <input
                className="modal-input"
                placeholder="z.B. Acme GmbH"
                value={tenantCreateName}
                onChange={e => setTenantCreateName(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && saveCreateTenant()}
                autoFocus
              />
              <p className="modal-hint">
                Du wirst automatisch Owner des neuen Mandanten. Mitglieder fügst du anschließend
                im Mandanten selbst hinzu.
              </p>
            </div>
            <div className="modal-actions">
              <button className="btn-sm-primary" onClick={saveCreateTenant} disabled={tenantCreateSaving}>
                {tenantCreateSaving ? '...' : 'Erstellen'}
              </button>
              <button className="btn-sm-cancel" onClick={() => setCreatingTenant(false)}>Abbrechen</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Tenants Tab ── */}
      {tab === 'tenants' && (
        tenantsLoading ? (
          <div className="admin-loading">Mandanten werden geladen...</div>
        ) : (
          <div className="admin-content">
            <div className="admin-section">
              <h3 className="admin-section-title">
                Alle Mandanten
                <button className="btn-sm-primary" style={{ marginLeft: 'auto', flex: '0 0 auto' }} onClick={openCreateTenant}>
                  + Mandant hinzufügen
                </button>
              </h3>
              <p className="admin-section-hint">
                Deaktivierte Mandanten sind für ihre Mitglieder gesperrt — niemand kann sie öffnen oder
                Daten darin bearbeiten. Zum Löschen muss ein Mandant zuerst deaktiviert sein.
              </p>
              {adminTenants.length === 0 ? (
                <div className="tenant-admin-empty">Noch keine Mandanten.</div>
              ) : (
                <div className="tenant-admin-list">
                  {adminTenants.map(t => (
                    <div key={t._id} className={`tenant-admin-card ${t.active ? '' : 'inactive'}`}>
                      <div className="tenant-admin-info">
                        <div className="tenant-admin-icon">🏢</div>
                        <div>
                          <div className="tenant-admin-name">
                            {t.name}
                            {!t.active && <span className="role-badge">deaktiviert</span>}
                          </div>
                          <div className="tenant-admin-meta">
                            {t.memberCount} {t.memberCount === 1 ? 'Mitglied' : 'Mitglieder'}
                            {t.slug ? ` · ${t.slug}` : ''}
                          </div>
                        </div>
                      </div>
                      <div className="tenant-admin-actions">
                        <button
                          className={t.active ? 'btn-deactivate' : 'btn-activate'}
                          onClick={() => toggleTenantActive(t)}
                          disabled={tenantBusy === t._id}
                        >
                          {tenantBusy === t._id ? '...' : t.active ? 'Deaktivieren' : 'Aktivieren'}
                        </button>
                        <button
                          className="btn-delete"
                          onClick={() => deleteTenant(t)}
                          disabled={tenantBusy === t._id || t.active}
                          title={t.active ? 'Erst deaktivieren, dann löschen' : 'Endgültig löschen'}
                        >
                          🗑️
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )
      )}

      {/* ── Models Tab ── */}
      {tab === 'models' && (
        modelsLoading ? (
          <div className="admin-loading">Modelle werden geladen...</div>
        ) : (
          <div className="admin-content">
            <div className="admin-section">
              <h3 className="admin-section-title">Modell-Anzeigenamen</h3>
              <p className="admin-section-hint">
                Vergib einen benutzerfreundlichen Namen für jedes Modell. Leer lassen = Originalname wird angezeigt.
              </p>
              {models.length === 0 ? (
                <div className="models-offline-hint">
                  ⚠️ Keine Modelle gefunden. Ist der KI-Server online?
                </div>
              ) : (
                <div className="model-name-list">
                  {models.map(m => (
                    <div key={m.name} className="model-name-card">
                      <div className="model-name-original">{m.name}</div>
                      <div className="model-name-input-row">
                        <input
                          type="text"
                          className="model-display-name-input"
                          placeholder="Anzeigename (optional)"
                          value={editingNames[m.name] ?? ''}
                          onChange={e => setEditingNames(prev => ({ ...prev, [m.name]: e.target.value }))}
                          onKeyDown={e => e.key === 'Enter' && saveDisplayName(m.name)}
                          onBlur={() => saveDisplayName(m.name)}
                        />
                        <button
                          className="btn-save-name"
                          onClick={() => saveDisplayName(m.name)}
                          disabled={savingModel === m.name}
                        >
                          {savingModel === m.name ? '...' : 'Speichern'}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )
      )}
    </div>
  );
}
